import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from './lib/utils';

const tabsListVariants = cva('inline-flex items-center', {
  variants: {
    variant: {
      default:
        'bg-muted text-muted-foreground h-9 w-fit justify-center rounded-lg p-[3px]',
      icon: 'h-auto gap-1 rounded-lg border bg-transparent p-1',
      toolbar:
        'h-auto w-full justify-start gap-0.5 overflow-x-auto overflow-y-hidden border-b bg-transparent p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
    },
  },
  defaultVariants: { variant: 'default' },
});

const tabsTriggerVariants = cva(
  "cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'data-[state=active]:bg-primary data-[state=active]:text-white dark:data-[state=active]:text-white text-foreground dark:text-muted-foreground hover:text-foreground dark:hover:text-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium data-[state=active]:shadow-sm',
        icon: 'data-[state=active]:bg-secondary data-[state=active]:hover:bg-secondary/90 data-[state=active]:text-secondary-foreground hover:bg-secondary/90 hover:text-secondary-foreground inline-flex size-9 flex-none items-center justify-center rounded-md p-0 data-[state=active]:shadow-none',
        toolbar:
          "text-muted-foreground hover:text-foreground data-[state=active]:text-foreground data-[state=active]:border-primary relative -mb-px inline-flex flex-none flex-col items-center justify-center gap-1 border-b-2 border-transparent px-3 pt-2.5 pb-2 text-[11px] font-medium tracking-wide uppercase md:px-4 md:text-xs focus-visible:bg-muted/50 rounded-t-md focus-visible:ring-0 focus-visible:outline-none [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
